// theme/presets.js
//
// Ready-to-spread sx object builders derived from tokens.js. Import these
// instead of hand-rolling button/heading/card/input styles per component —
// this is the file tokens.js's comments point to.

import { tokens } from "./tokens";

const fam = (key) => (key === "heading" ? tokens.font.heading : tokens.font.body);

// Typography — pulls fontFamily/size/weight/spacing straight from
// tokens.type.<variant>, resolving "heading"/"body" to the real font stack.
export const heading = (variant = "h2", color = tokens.color.ink) => {
  const spec = tokens.type[variant];
  return {
    fontFamily: fam(spec.fontFamily),
    fontSize: spec.fontSize,
    lineHeight: spec.lineHeight,
    fontWeight: spec.fontWeight,
    letterSpacing: spec.letterSpacing,
    color,
  };
};

export const bodyText = (variant = "body1", color = tokens.color.inkMuted) => {
  const spec = tokens.type[variant];
  return {
    fontFamily: fam(spec.fontFamily),
    fontSize: spec.fontSize,
    lineHeight: spec.lineHeight,
    fontWeight: spec.fontWeight,
    letterSpacing: spec.letterSpacing,
    textTransform: spec.textTransform,
    color,
  };
};

// Buttons — size maps to tokens.button.{height,paddingX,fontSize}; variant
// picks the color treatment. Radius always comes from tokens.button.radius
// (8px) — pill shapes are reserved for badges/chips via pillBadgeSx.
export const buttonSx = (size = "lg", variant = "solid") => {
  const base = {
    height: tokens.button.height[size],
    paddingLeft: `${tokens.button.paddingX[size]}px`,
    paddingRight: `${tokens.button.paddingX[size]}px`,
    fontFamily: tokens.font.body,
    fontSize: tokens.button.fontSize[size],
    fontWeight: 600,
    textTransform: "none",
    borderRadius: `${tokens.button.radius}px`,
    boxShadow: "none",
    transition: "background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease",
  };

  if (variant === "outline") {
    return {
      ...base,
      bgcolor: "transparent",
      color: tokens.color.ink,
      border: `1px solid ${tokens.color.line}`,
      "&:hover": {
        bgcolor: tokens.color.tealLight,
        borderColor: tokens.color.teal,
        color: tokens.color.teal,
      },
    };
  }

  if (variant === "tint") {
    return {
      ...base,
      bgcolor: tokens.color.tealLight,
      color: tokens.color.teal,
      "&:hover": { bgcolor: tokens.color.teal, color: tokens.color.white },
    };
  }

  if (variant === "dark") {
    return {
      ...base,
      bgcolor: tokens.color.ink,
      color: tokens.color.white,
      "&:hover": { bgcolor: tokens.color.tealDark },
    };
  }

  // solid (default) — primary brand CTA
  return {
    ...base,
    bgcolor: tokens.color.teal,
    color: tokens.color.white,
    "&:hover": { bgcolor: tokens.color.tealDark },
  };
};

// Small pill-shaped tags — eyebrow badges, keyword chips. The one place
// tokens.radius.pill (999px) is used, since real buttons stay boxy (8px).
export const pillBadgeSx = (bg = tokens.color.tealLight, color = tokens.color.teal) => ({
  display: "inline-flex",
  alignItems: "center",
  width: "fit-content",
  px: 1.75,
  py: 0.5,
  borderRadius: `${tokens.radius.pill}px`,
  bgcolor: bg,
  color,
  fontFamily: tokens.font.body,
  fontWeight: 700,
  fontSize: tokens.type.caption.fontSize,
});

// Cards — variant picks background/border/shadow from tokens.card.<variant>;
// radius and padding come from the shared tokens.card values.
export const cardSx = (variant = "elevated") => {
  const spec = tokens.card[variant];
  return {
    background: spec.background,
    border: spec.border,
    borderRadius: `${tokens.card.radius}px`,
    boxShadow: spec.shadowResting,
    transition: "box-shadow 0.25s ease, border-color 0.25s ease",
    "&:hover": {
      boxShadow: spec.shadowHover,
      ...(spec.borderHover !== "transparent" && { borderColor: spec.borderHover }),
    },
  };
};

// Inputs — one spec for every TextField/Select/search box on the site.
export const inputSx = (size = "md") => ({
  height: tokens.input.height[size],
  fontFamily: tokens.font.body,
  fontSize: tokens.input.fontSize[size],
  borderRadius: `${tokens.input.radius}px`,
  bgcolor: tokens.input.bg,
  "& .MuiOutlinedInput-notchedOutline": { borderColor: tokens.input.border },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: tokens.input.borderHover },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: tokens.input.borderFocus,
    borderWidth: 1.5,
  },
});
